import loadCounters from './services/counters'
import loadProducts from './services/products'
import loadProductsByName from './services/products/name'
import loadPriceTransparency from './services/price-transparency'
import loadMap from './services/map'
import loadGeoData from './services/geo-data/with-se-object'
import loadGeoDataWithEngineId from './services/geo-data/with-se-id'
import loadStartDates from './services/dates/start'
import loadEndDates from './services/dates/end'

import eventNames from './events/names'
import initEvents from './events'

let locale = null

const initServer = ({ instance, events }) => {
    const setLocale = (_locale) => {
        locale = _locale
    }

    const getSeRequest = (options) => {
        return instance.store.getSeRequest(options)
    }
    const getSeRequestProductsByName = (name, options) => {
        return instance.store.getSeRequestProductsByName(name, options)
    }
    const getSeRequestProductsById = (ids) => {
        return instance.store.getSeRequestProductsById(ids)
    }
    const getSeRequestProductsByPrestId = (ids, options) => {
        return instance.store.getSeRequestProductsByPrestId(ids, options)
    }
    const getStartDatesRequest = () => {
        return instance.store.getStartDatesRequest()
    }
    const getEndDatesRequest = (startDate) => {
        return instance.store.getEndDatesRequest(startDate)
    }
    const getProductsQueryParams = () => {
        return instance.store.getProductsQueryParams()
    }
    const setProductsResponse = (result) => {
        instance.store.setProductsResponse(result)
    }
    const setCountersResponse = (result) => {
        instance.store.setCountersResponse(result)
    }
    const getEngineId = () => {
        return instance.store.getEngineId()
    }

    const request = (...props) => {
        return instance.options.type === 'counters'
            ? requestCounters(...props)
            : instance.options.type === 'products'
            ? requestProducts(...props)
            : undefined
    }

    let requestCounter = 0
    const requestCounters = async () => {
        const _requestCounter = ++requestCounter
        events.fire(eventNames.BEFORE_COUNTERS_REQUEST, { request })
        requestsInProgressNum++
        const result = await _requestCounters().catch((e) => {
            requestsInProgressNum--
            throw e
        })
        if (_requestCounter === requestCounter) {
            setCountersResponse(result)
        }
        requestsInProgressNum--
        events.fire(eventNames.AFTER_COUNTERS_REQUEST)
    }

    const _requestCounters = () => {
        const seRequest = getSeRequest()
        const request = {
            ...seRequest,
            expectedReturn: {
                ...seRequest.expectedReturn,
                counterBy: instance.options.counterBy
            }
        }
        const requestOptions = instance.options.requestOptions()
        return loadCounters(null, request, requestOptions)
    }

    const _requestProducts = () => {
        const request = getSeRequest({ counters: false })
        const params = getProductsQueryParams()
        const requestOptions = instance.options.requestOptions()
        return loadProducts(params, request, requestOptions)
    }

    const requestProducts = async () => {
        const _requestCounter = ++requestCounter
        await Promise.all([
            (async () => {
                events.fire(eventNames.BEFORE_COUNTERS_REQUEST)
                requestsInProgressNum++
                const result = await _requestCounters().catch((e) => {
                    requestsInProgressNum--
                    throw e
                })
                if (_requestCounter === requestCounter) {
                    setCountersResponse(result)
                }
                requestsInProgressNum--
                events.fire(eventNames.AFTER_COUNTERS_REQUEST)
            })(),
            (async () => {
                events.fire(eventNames.BEFORE_PRODUCTS_REQUEST)
                requestsInProgressNum++
                const result = await _requestProducts().catch((e) => {
                    requestsInProgressNum--
                    throw e
                })
                if (_requestCounter === requestCounter) {
                    setProductsResponse(result)
                }
                requestsInProgressNum--
                events.fire(eventNames.AFTER_PRODUCTS_REQUEST)
            })()
        ])
    }

    const requestStartDates = async (month, nextMonth) => {
        const request = getStartDatesRequest()
        events.fire(eventNames.BEFORE_START_DATES_REQUEST, { request })
        requestsInProgressNum++
        const requestOptions = instance.options.requestOptions()
        const result = await loadStartDates({ month, nextMonth }, request, requestOptions).catch((e) => {
            requestsInProgressNum--
            throw e
        })
        requestsInProgressNum--
        events.fire(eventNames.AFTER_START_DATES_REQUEST, { request, result })
        return result
    }

    const requestEndDates = async (month, nextMonth, startDate) => {
        const request = getEndDatesRequest(startDate)
        events.fire(eventNames.BEFORE_END_DATES_REQUEST, { request })
        requestsInProgressNum++
        const requestOptions = instance.options.requestOptions()
        const result = await loadEndDates({ month, nextMonth }, request, requestOptions).catch((e) => {
            requestsInProgressNum--
            throw e
        })
        requestsInProgressNum--
        events.fire(eventNames.AFTER_END_DATES_REQUEST, { request, result })
        return result
    }

    const requestMap = async () => {
        const request = getSeRequest()
        events.fire(eventNames.BEFORE_MAP_REQUEST, { request })
        requestsInProgressNum++
        const requestOptions = instance.options.requestOptions()
        const result = await loadMap(request, requestOptions).catch((e) => {
            requestsInProgressNum--
            throw e
        })
        requestsInProgressNum--
        instance.store.setMapResponse(result)
        events.fire(eventNames.AFTER_MAP_REQUEST, { request, result })
    }

    const requestGeoData = async () => {
        const request = instance.store.getSeRequestBase()
        events.fire(eventNames.BEFORE_MAP_REQUEST, { request })
        requestsInProgressNum++
        const requestOptions = instance.options.requestOptions()
        const result = await loadGeoData(request, requestOptions).catch((e) => {
            requestsInProgressNum--
            throw e
        })
        requestsInProgressNum--
        instance.store.setGeoResponse(result)
        events.fire(eventNames.AFTER_MAP_REQUEST, { request, result })
    }
    const requestGeoDataWithEngineId = async () => {
        const engineId = getEngineId()
        events.fire(eventNames.BEFORE_MAP_REQUEST)
        requestsInProgressNum++
        const requestOptions = instance.options.requestOptions()
        const result = await loadGeoDataWithEngineId(engineId, requestOptions).catch((e) => {
            requestsInProgressNum--
            throw e
        })

        requestsInProgressNum--
        instance.store.setGeoResponse(result)
        events.fire(eventNames.AFTER_MAP_REQUEST)

        return result
    }

    // side method
    const requestProductsByName = async (liheName, options) => {
        const request = getSeRequestProductsByName(liheName, options)
        const requestOptions = instance.options.requestOptions()
        return await loadProductsByName({ size: 10 }, request, requestOptions)
    }

    // side method
    const requestProductsById = async (ids) => {
        const request = getSeRequestProductsById(ids)
        const requestOptions = instance.options.requestOptions()
        return await loadProducts(null, request, requestOptions)
    }
    const requestProductsByPrestId = async (ids, options = {}) => {
        const request = getSeRequestProductsByPrestId(ids, options)
        const requestOptions = instance.options.requestOptions()
        return await loadProducts(null, request, requestOptions)
    }

    // side method
    const requestPriceTransparency = async ({ liheId, packCode, startDate, endDate, season, participantsAges }) => {
        const requestOptions = instance.options.requestOptions()
        const data = {
            travellers: participantsAges || instance.cookies.getParticipantsAges()
        }
        const params = {
            liheId,
            packCode,
            startDate: startDate || instance.store.getFieldValue('beginDate'),
            endDate: endDate || instance.store.getFieldValue('endDate')
        }
        if (season) {
            params.season = season
        }
        return await loadPriceTransparency(data, params, requestOptions)
    }

    // side method
    const requestPriceTransparencyPriceForProduct = async ({ product, prestation, participantsAges }) => {
        const season = instance.store.getSeason()
        const result = await requestPriceTransparency({
            liheId: product.liheId,
            packCode: prestation.packCode,
            startDate: prestation.startDate,
            endDate: prestation.endDate,
            season,
            participantsAges
        })
        if (!result || !result.prestations) {
            return null
        }
        return (
            result.prestations.find((p) => {
                return (
                    p.prestIdSource === prestation.id &&
                    p.startDate === prestation.startDate &&
                    p.endDate === prestation.endDate &&
                    p.codePackage === prestation.packCode &&
                    p.duration === prestation.duration &&
                    p.skipass === prestation.skipass
                )
            }) || null
        )
    }

    let requestsInProgressNum = 0
    const getRequestsInProgressNum = () => {
        return requestsInProgressNum
    }

    return {
        events: eventNames,
        on: events.on,
        once: events.once,
        off: events.off,

        setLocale,

        request,
        requestCounters,
        requestProducts,
        requestMap,
        requestGeoData,
        requestGeoDataWithEngineId,
        requestStartDates,
        requestEndDates,

        getRequestsInProgressNum,

        // side services
        requestProductsByName,
        requestProductsById,
        requestProductsByPrestId,
        requestPriceTransparency,
        requestPriceTransparencyPriceForProduct
    }
}

const preInitServer = (instance) => {
    const events = initEvents(instance)
    return {
        eventNames,
        events,
        init: () => initServer({ instance, events })
    }
}

export default preInitServer
